import React, { useState, useEffect } from 'react';
import { 
    BrainCircuit, 
    Palette, 
    Megaphone, 
    X, 
    ChevronRight, 
    CheckCircle, 
    Rocket,
    Play,
    ArrowRight
} from 'lucide-react';
import { doc, updateDoc, getDoc } from 'firebase/firestore';

// --- ASSETS & STYLES ---
// Reusing your button style for consistency, but elevating it for the intro
const OnboardingButton = ({ children, onClick, variant = 'primary', className = '', icon: Icon }) => {
    const baseStyle = "inline-flex items-center justify-center font-bold rounded-xl transition-all duration-300 ease-out transform hover:scale-[1.02] active:scale-[0.98] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-offset-black";
    
    const variants = {
        primary: "bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-500 hover:to-indigo-500 text-white shadow-lg shadow-purple-500/30 border border-purple-500/50",
        secondary: "bg-zinc-800 hover:bg-zinc-700 text-zinc-200 border border-zinc-700",
        ghost: "bg-transparent hover:bg-white/5 text-zinc-400 hover:text-white"
    };

    return (
        <button onClick={onClick} className={`${baseStyle} ${variants[variant]} px-6 py-4 text-lg ${className}`}>
            {children}
            {Icon && <Icon className="ml-2 w-5 h-5" />}
        </button>
    );
};

// --- SLIDE CONTENT DATA ---
const ONBOARDING_STEPS = [
    {
        id: 'welcome',
        type: 'intro',
        title: "Welcome to the Future of Film Marketing",
        subtitle: "You've just unlocked the ultimate AI toolkit for filmmakers. Let's get your project greenlit, distributed, and seen.",
        icon: Rocket
    },
    {
        id: 'audience',
        type: 'feature',
        title: "Strategy First: Audience Genius",
        description: "Stop guessing who your audience is. Our AI analyzes your script and themes to build precise viewer personas, target markets, and festival strategies.",
        icon: BrainCircuit,
        color: "text-pink-500",
        bg: "bg-pink-500/10",
        border: "border-pink-500/20",
        image: "https://images.unsplash.com/photo-1517245386807-bb43f82c33c4?auto=format&fit=crop&q=80&w=800" // Placeholder representing strategy
    },
    {
        id: 'creative',
        type: 'feature',
        title: "Creative Power: Visual Lab",
        description: "A/B test your movie posters before you print them. Generate infinite ad concepts and remix audience reactions into viral social content.",
        icon: Palette,
        color: "text-cyan-400",
        bg: "bg-cyan-400/10",
        border: "border-cyan-400/20",
        image: "https://images.unsplash.com/photo-1626814026160-2237a95fc5a0?auto=format&fit=crop&q=80&w=800" // Placeholder representing visuals
    },
    {
        id: 'execution',
        type: 'feature',
        title: "Execution: Press & Campaigns",
        description: "Launch campaigns in seconds. Generate full press kits, discover the perfect influencers, and track your budget vs. actual performance in real-time.",
        icon: Megaphone,
        color: "text-purple-500",
        bg: "bg-purple-500/10",
        border: "border-purple-500/20",
        image: "https://images.unsplash.com/photo-1557804506-669a67965ba0?auto=format&fit=crop&q=80&w=800" // Placeholder representing execution
    }
];

export const OnboardingFlow = ({ user, db, onComplete }) => {
    const [currentStepIndex, setCurrentStepIndex] = useState(0);
    const [isVisible, setIsVisible] = useState(true);
    const [isCompleting, setIsCompleting] = useState(false);

    const currentStep = ONBOARDING_STEPS[currentStepIndex];
    const isLastStep = currentStepIndex === ONBOARDING_STEPS.length - 1;

    // Logic to finish onboarding and save state to Firebase
    const handleFinish = async () => {
        setIsCompleting(true);
        try {
            if (user && db) {
                const userRef = doc(db, 'users', user.uid);
                await updateDoc(userRef, {
                    hasCompletedOnboarding: true,
                    onboardingDate: new Date()
                });
            }
            // Allow animation to play out
            setTimeout(() => {
                setIsVisible(false);
                if (onComplete) onComplete();
            }, 500);
        } catch (error) {
            console.error("Error saving onboarding status:", error);
            // Still close on error so user isn't stuck
            setIsVisible(false);
            if (onComplete) onComplete();
        }
    };

    const handleNext = () => {
        if (isLastStep) {
            handleFinish();
        } else {
            setCurrentStepIndex(prev => prev + 1);
        }
    };

    if (!isVisible) return null;

    return (
        <div className={`fixed inset-0 z-[100] flex items-center justify-center bg-black/90 backdrop-blur-xl transition-opacity duration-500 ${isCompleting ? 'opacity-0' : 'opacity-100'}`}>
            
            {/* Background Ambience */}
            <div className="absolute inset-0 overflow-hidden pointer-events-none">
                <div className={`absolute top-1/4 left-1/4 w-96 h-96 bg-purple-600/20 rounded-full blur-[120px] transition-all duration-1000 ${currentStepIndex % 2 === 0 ? 'translate-x-0' : 'translate-x-32'}`} />
                <div className={`absolute bottom-1/4 right-1/4 w-96 h-96 bg-blue-600/20 rounded-full blur-[120px] transition-all duration-1000 ${currentStepIndex % 2 === 0 ? 'translate-y-0' : '-translate-y-32'}`} />
            </div>

            <div className="relative w-full max-w-5xl p-4 md:p-8 flex flex-col md:flex-row gap-12 items-center justify-center h-full md:h-auto">
                
                {/* LEFT COLUMN: Visuals & Progress */}
                <div className="w-full md:w-1/2 space-y-8 text-center md:text-left">
                    {/* Progress Bars */}
                    <div className="flex gap-2 justify-center md:justify-start mb-8">
                        {ONBOARDING_STEPS.map((_, idx) => (
                            <div 
                                key={idx} 
                                className={`h-1.5 rounded-full transition-all duration-500 ${idx <= currentStepIndex ? 'w-8 bg-purple-500' : 'w-2 bg-zinc-800'}`}
                            />
                        ))}
                    </div>

                    <div className="space-y-6 animate-fade-in-up">
                        {currentStep.type === 'intro' ? (
                            <div className="inline-flex items-center justify-center w-20 h-20 rounded-2xl bg-gradient-to-br from-zinc-800 to-black border border-zinc-700 shadow-2xl mb-4">
                                <Rocket className="w-10 h-10 text-purple-400" />
                            </div>
                        ) : (
                            <div className={`inline-flex items-center justify-center w-16 h-16 rounded-xl ${currentStep.bg} ${currentStep.border} border mb-4`}>
                                {React.createElement(currentStep.icon, { className: `w-8 h-8 ${currentStep.color}` })}
                            </div>
                        )}
                        
                        <h1 className="text-4xl md:text-5xl font-bold text-white leading-tight font-poppins">
                            {currentStep.title}
                        </h1>
                        
                        <p className="text-xl text-zinc-400 leading-relaxed max-w-lg mx-auto md:mx-0">
                            {currentStep.subtitle || currentStep.description}
                        </p>

                        <div className="pt-4 flex flex-col md:flex-row gap-4 justify-center md:justify-start">
                            <OnboardingButton 
                                onClick={handleNext} 
                                icon={isLastStep ? CheckCircle : ArrowRight}
                            >
                                {currentStep.type === 'intro' ? "Start the Tour" : isLastStep ? "Get Started" : "Next Feature"}
                            </OnboardingButton>
                            
                            {!isLastStep && (
                                <OnboardingButton 
                                    variant="ghost" 
                                    onClick={handleFinish}
                                    className="text-sm"
                                >
                                    Skip Tour
                                </OnboardingButton>
                            )}
                        </div>
                    </div>
                </div>

                {/* RIGHT COLUMN: Dynamic Visual Preview */}
                <div className="w-full md:w-1/2 h-[400px] md:h-[500px] relative hidden md:block">
                    <div className="absolute inset-0 bg-zinc-900 rounded-2xl border border-zinc-800 shadow-2xl overflow-hidden transform rotate-3 transition-all duration-700 hover:rotate-0">
                         {/* Mock UI Header */}
                         <div className="h-8 bg-zinc-800 border-b border-zinc-700 flex items-center px-4 gap-2">
                             <div className="w-3 h-3 rounded-full bg-red-500/50"></div>
                             <div className="w-3 h-3 rounded-full bg-yellow-500/50"></div>
                             <div className="w-3 h-3 rounded-full bg-green-500/50"></div>
                         </div>
                         
                         {/* Dynamic Content based on Step */}
                         <div className="p-6 h-full relative">
                            {currentStep.type === 'intro' && (
                                <div className="flex flex-col items-center justify-center h-full text-zinc-700 space-y-4">
                                    <div className="w-32 h-32 rounded-full bg-gradient-to-tr from-purple-500/20 to-blue-500/20 animate-pulse flex items-center justify-center">
                                        <Play className="w-12 h-12 text-purple-500/50 ml-1" />
                                    </div>
                                    <p className="font-mono text-sm">Initializing Dashboard...</p>
                                </div>
                            )}

                            {currentStep.type === 'feature' && (
                                <div className="animate-fade-in h-full flex flex-col">
                                    <div className="absolute inset-0 bg-gradient-to-b from-transparent to-zinc-900/90 z-10"></div>
                                    <img 
                                        src={currentStep.image} 
                                        alt="Feature Preview" 
                                        className="absolute inset-0 w-full h-full object-cover opacity-50"
                                    />
                                    <div className="relative z-20 mt-auto pb-12">
                                        <div className="bg-black/60 backdrop-blur-md p-4 rounded-xl border border-white/10">
                                            <div className="flex items-center gap-3 mb-2">
                                                {React.createElement(currentStep.icon, { size: 18, className: "text-white" })}
                                                <span className="text-white font-bold text-sm uppercase tracking-wider">AI Analysis Complete</span>
                                            </div>
                                            <div className="h-2 w-full bg-zinc-700 rounded-full overflow-hidden">
                                                <div className="h-full bg-gradient-to-r from-purple-500 to-blue-500 w-[92%]"></div>
                                            </div>
                                            <div className="flex justify-between text-xs text-zinc-400 mt-2">
                                                <span>Processing Data...</span>
                                                <span className="text-white font-mono">92% Match</span>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            )}
                         </div>
                    </div>
                    
                    {/* Decorative elements behind the card */}
                    <div className="absolute -inset-4 bg-gradient-to-r from-purple-600 to-blue-600 rounded-3xl opacity-20 blur-2xl -z-10"></div>
                </div>
            </div>
        </div>
    );
};